import { routes } from "../apiroutes";
import HttpService from "../httpServices";
import useFetchItem from "../useFetchItem";
import useMutateItem from "../useMutateItem";
import { ErrorHandler, extractApiErrorDetail } from "../errorHandler";
import { showErrorAlert, showSuccessAlert } from "../../utils/alert";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const httpService = new HttpService();

/** Pick leave entry type whose name is "Leave" (case-insensitive), per backend contract. */
export const pickEssLeaveEntryTypeId = (leaveEntryTypes) => {
  if (!Array.isArray(leaveEntryTypes)) return null;
  const row = leaveEntryTypes.find(
    (t) => String(t?.name || "").trim().toLowerCase() === "leave",
  );
  return row?.id || null;
};

export const downloadEssOnboardingFile = async (documentId) => {
  const svc = new HttpService();
  const res = await svc.getBlob(routes.essMeDocumentFile(documentId));
  const blob = res.data;
  const disposition = res.headers["content-disposition"] || "";
  let filename = "document";
  const quoted = /filename="([^"]+)"/i.exec(disposition);
  const unquoted = /filename=([^;\s]+)/i.exec(disposition);
  if (quoted?.[1]) filename = quoted[1];
  else if (unquoted?.[1]) filename = unquoted[1].replace(/^["']|["']$/g, "");

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Open document blob in a new tab (preview). Revokes object URL after a delay. */
export const openEssOnboardingFileInNewTab = async (documentId) => {
  const svc = new HttpService();
  const res = await svc.getBlob(routes.essMeDocumentFile(documentId));
  const blob = res.data;
  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    window.URL.revokeObjectURL(url);
    throw new Error("Pop-up blocked. Try Download instead.");
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 300_000);
};

export const useEssDashboard = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["essDashboard"],
    queryFn: () => httpService.getData(routes.essDashboard()),
    enabled,
    retry: 1,
  });

  const payload = data?.data;

  return {
    essDashboardIsFetched: isFetched,
    essDashboardIsLoading: isLoading,
    essDashboardIsFetching: isFetching,
    essDashboardError: error,
    essDashboard: payload,
    /** Shorthand for display-oriented bundle */
    portal: payload?.portal,
    linked: payload?.linked,
    refetchEssDashboard: refetch,
  };
};

export const useEssLeaveReportingSeniors = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["essLeaveReportingSeniors"],
    queryFn: () => httpService.getData(routes.essLeaveReportingSeniors()),
    enabled,
    retry: 1,
  });

  const raw = data?.data;
  const list = raw?.reporting_seniors;
  const rows = Array.isArray(list) ? list : [];

  return {
    reportingSeniorsIsFetched: isFetched,
    reportingSeniorsIsLoading: isLoading,
    reportingSeniorsIsFetching: isFetching,
    reportingSeniorsError: error,
    reportingSeniors: rows,
    refetchReportingSeniors: refetch,
  };
};

export const useEssMe = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["essMe"],
    queryFn: () => httpService.getData(routes.essMe()),
    enabled,
    retry: 1,
  });

  const payload = data?.data;

  return {
    essMeIsFetched: isFetched,
    essMeIsLoading: isLoading,
    essMeIsFetching: isFetching,
    essMeError: error,
    essMe: payload,
    refetchEssMe: refetch,
  };
};

export const useEssLeaveMyRequests = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["essLeaveMyRequests"],
    queryFn: () => httpService.getData(routes.essLeaveMyRequests()),
    enabled,
    retry: 1,
  });

  const list = data?.data;
  const rows = Array.isArray(list) ? list : [];

  return {
    leaveRequestsIsFetched: isFetched,
    leaveRequestsIsLoading: isLoading,
    leaveRequestsIsFetching: isFetching,
    leaveRequestsError: error,
    leaveRequests: rows,
    refetchLeaveRequests: refetch,
  };
};

export const useEssOvertimeMyRequests = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["essOvertimeMyRequests"],
    queryFn: () => httpService.getData(routes.essOvertimeMyRequests()),
    enabled,
    retry: 1,
  });

  const list = data?.data;
  const rows = Array.isArray(list) ? list : [];

  return {
    overtimeRequestsIsFetched: isFetched,
    overtimeRequestsIsLoading: isLoading,
    overtimeRequestsIsFetching: isFetching,
    overtimeRequestsError: error,
    overtimeRequests: rows,
    refetchOvertimeRequests: refetch,
  };
};

export const useEssLeaveRemind = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leaveId) =>
      httpService.postData({ leave_id: leaveId }, routes.essLeaveRemind()),
    onSuccess: (res) => {
      const body = res?.data || {};
      showSuccessAlert(body?.message || "Reminder sent");
      queryClient.invalidateQueries({ queryKey: ["essLeaveMyRequests"] });
      queryClient.invalidateQueries({ queryKey: ["essDashboard"] });
    },
    onError: (err) => {
      const ex = extractApiErrorDetail(err);
      showErrorAlert(ex.message || "Could not send reminder.");
    },
  });
};

export const useEssLeaveRecall = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (leaveId) =>
      httpService.postData({ leave_id: leaveId }, routes.essLeaveRecall()),
    onSuccess: (res) => {
      const body = res?.data || {};
      showSuccessAlert(body?.message || "Leave recalled");
      queryClient.invalidateQueries({ queryKey: ["essLeaveMyRequests"] });
      queryClient.invalidateQueries({ queryKey: ["essDashboard"] });
    },
    onError: (err) => {
      const ex = extractApiErrorDetail(err);
      showErrorAlert(ex.message || "Could not recall this leave.");
    },
  });
};

export const useEssLeaveApply = () => {
  const { isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.postData(payload, routes.essLeaveApply()),
    queryKeys: ["essLeaveMyRequests", "essDashboard"],
    onSuccess: (res) => {
      const body = res?.data || {};
      showSuccessAlert(body?.message || "Leave request submitted");
    },
    onError: (err) => showErrorAlert(ErrorHandler(err)),
  });

  return { leaveApplyIsLoading: isPending, leaveApply: (p) => mutate(p) };
};

export const useEssOvertimeSubmit = () => {
  const { isPending, mutate } = useMutateItem({
    mutationFn: (payload) => httpService.postData(payload, routes.essOvertimeRequest()),
    queryKeys: ["essOvertimeMyRequests", "essDashboard"],
    onSuccess: (res) => {
      const body = res?.data || {};
      showSuccessAlert(body?.message || "Overtime request submitted");
    },
    onError: (err) => showErrorAlert(ErrorHandler(err)),
  });

  return { overtimeSubmitIsLoading: isPending, overtimeSubmit: (p) => mutate(p) };
};
